import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dockyardImg from "../assets/dockyard.png";
import { logoutUser } from "../actions/AuthActions";
import LocationIndicator from "./LocationIndicator";
import GPSEnableNotification from "./GPSEnableNotification";

const EmployeeHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useSelector((state) => state.auth);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [time, setTime] = useState(new Date());

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      dispatch(logoutUser());
      navigate("/login");
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const currentTime = useMemo(
    () =>
      time.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
    [time],
  );

  const navLinks = [
    { name: "Dashboard", path: "/employee/dashboard" },
    { name: "Booking", path: "/employee/schedule-booking" },
    { name: "Track My Bookings", path: "/employee/my-bookings" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    // bug 8 fix - changed from sticky to fixed to prevent header from disappearing when scrolling down
    // <header className="bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg sticky top-0 z-50">
    <header className="bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg fixed top-0 left-0 right-0 z-50 safe-area-header">
      <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4">
        {/* Logo + Title + Clock */}
        <div className="flex items-center gap-4">
          <img
            src={dockyardImg}
            alt="Dockyard Logo"
            className="h-12 w-12 rounded-lg shadow-md object-cover border border-blue-700"
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-5">
            <h1 className="text-2xl font-extrabold tracking-tight">
              Aero View | Operations
            </h1>
            <div className="flex items-center gap-2 mt-1 sm:mt-0">
              <svg
                className="w-4 h-4 text-blue-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="px-3 py-1 bg-blue-700/50 rounded-md font-mono text-sm tracking-wider">
                {currentTime}
              </span>
              <div className="ml-3">
                <LocationIndicator />
              </div>
            </div>
          </div>
        </div>

        <GPSEnableNotification />

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`relative font-medium transition-all duration-200 px-1 ${
                isActive(link.path)
                  ? "text-yellow-300 after:absolute after:bottom-[-6px] after:left-0 after:w-full after:h-0.5 after:bg-yellow-300"
                  : "text-gray-100 hover:text-white"
              }`}
            >
              {link.name}
            </Link>
          ))}

          <button
            onClick={handleLogout}
            disabled={loading}
            className={`px-5 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
              loading
                ? "bg-red-700/70 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700 shadow-md hover:shadow-lg"
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    opacity="0.3"
                  />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Logging out...
              </>
            ) : (
              "Logout"
            )}
          </button>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-blue-700/50 transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation menu"
          aria-expanded={mobileMenuOpen}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {mobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <nav className="md:hidden border-t border-blue-700/50 bg-blue-900/95 backdrop-blur-sm">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  isActive(link.path)
                    ? "bg-blue-700 text-yellow-300"
                    : "text-gray-100 hover:bg-blue-800"
                }`}
              >
                {link.name}
              </Link>
            ))}

            <button
              onClick={handleLogout}
              disabled={loading}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                loading
                  ? "bg-red-700/70 text-gray-300"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      opacity="0.3"
                    />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Logging out...
                </>
              ) : (
                "Logout"
              )}
            </button>
          </div>
        </nav>
      )}
    </header>
  );
};

export default EmployeeHeader;
